import { type BigDecimal, createBigDecimal } from '@0xtorch/big-decimal'
import z from 'zod'
import type { Chain } from '../../chain'

type Balance = {
  assetId: bigint
  amount: BigDecimal
}

type GetAssetBalances = (params: {
  address: string
  chain: Chain
  timeout?: number
}) => Promise<Balance[]>

export const getAssetBalances: GetAssetBalances = async (params) => {
  const balances = await Promise.all([
    getNativeBalances(params),
    getOtherBalances(params),
  ])
  return balances.flat()
}

const SystemAccountSchema = z.object({
  data: z.object({
    free: z.union([z.string(), z.number().int()]),
  }),
})

const AssetAccountSchema = z
  .object({
    balance: z.union([
      z.number().transform((v) => BigInt(Math.floor(v))),
      z
        .string()
        .regex(/^0x[\da-f]+$/i)
        .transform(BigInt),
    ]),
  })
  .nullable()

const TokenAccountSchema = z
  .object({
    free: z.union([
      z.number().transform((v) => BigInt(Math.floor(v))),
      z
        .string()
        .regex(/^0x[\da-f]+$/i)
        .transform(BigInt),
    ]),
  })
  .nullable()

/** native asset の保有量を取得する */
const getNativeBalances: GetAssetBalances = async ({
  address,
  chain,
  timeout,
}) => {
  const balance = await chain.client.call(async (api) => {
    if (
      api.query.system === undefined ||
      api.query.system.account === undefined
    ) {
      throw new Error('api.query.system.account is undefined')
    }
    const response = await api.query.system.account(address)
    const account = SystemAccountSchema.parse(response.toJSON())
    return typeof account.data.free === 'string'
      ? BigInt(account.data.free)
      : BigInt(Math.floor(account.data.free))
  }, timeout)
  return [
    {
      assetId: 0n,
      amount: createBigDecimal(balance, chain.nativeToken.decimals),
    },
  ]
}

/** native 以外の asset の保有量を取得する */
const getOtherBalances: GetAssetBalances = async ({
  address,
  chain,
  timeout,
}) => {
  if (chain.assetMap.size === 0) {
    return []
  }
  switch (chain.assetType) {
    case 'assets': {
      return await getAssetsBalances({
        address,
        chain,
        timeout,
      })
    }
    case 'tokens': {
      return await getTokensBalances({
        address,
        chain,
        timeout,
      })
    }
    default: {
      throw new Error(
        `Unsupported asset type: ${chain.assetType satisfies never}`,
      )
    }
  }
}

/** type ='assets' の保有量を取得する */
const getAssetsBalances: GetAssetBalances = async ({
  address,
  chain,
  timeout,
}) => {
  const assets = [...chain.assetMap.entries()]
  const balances = await chain.client.call(async (api) => {
    if (
      api.query.assets === undefined ||
      api.query.assets.account === undefined
    ) {
      throw new Error('api.query.assets.account is undefined')
    }
    const responses = await api.query.assets.account.multi(
      assets.map(([assetId]) => [assetId, address]),
    )
    return responses.map((response) => {
      const asset = AssetAccountSchema.parse(response.toJSON())
      return asset?.balance ?? 0n
    })
  }, timeout)
  return balances.map((balance, index) => ({
    assetId: assets[index][0],
    amount: createBigDecimal(balance, assets[index][1].decimals),
  }))
}

/** type ='tokens' の保有量を取得する */
const getTokensBalances: GetAssetBalances = async ({
  address,
  chain,
  timeout,
}) => {
  const assets = [...chain.assetMap.entries()]
  const balances = await chain.client.call(async (api) => {
    if (
      api.query.tokens === undefined ||
      api.query.tokens.accounts === undefined
    ) {
      throw new Error('api.query.tokens.accounts is undefined')
    }
    const responses = await api.query.tokens.accounts.multi(
      assets.map(([assetId]) => [address, assetId]),
    )
    return responses.map((response) => {
      const asset = TokenAccountSchema.parse(response.toJSON())
      return asset?.free ?? 0n
    })
  }, timeout)
  return balances.map((balance, index) => ({
    assetId: assets[index][0],
    amount: createBigDecimal(balance, assets[index][1].decimals),
  }))
}
